import { Autocomplete, AutocompleteProps, ListItemText, MenuItem, TextField } from '@mui/material'
import { CatalogProduct, CatalogProductQuery } from '../types'
import { useEffect, useState } from 'react'
import { getCatalogProducts } from '../services/catalogProductService'
import { useTranslation } from 'react-i18next'
import { CatalogProductStatus, CatalogProductType } from '../enums'
import CatalogProductDetailsList from './CatalogProductDetailsList'
import {
  isAuroraCatalogProduct,
  isElectricalCabinetOrSpineModuleCatalogProduct,
  isHeaterCatalogProduct
} from '../utils/catalogProduct'

type CatalogProductGroupBy = 'manufacturer' | 'fuse' | 'heatingPower'

function getFuse(catalogProduct: CatalogProduct) {
  return isElectricalCabinetOrSpineModuleCatalogProduct(catalogProduct)
    ? catalogProduct.customData?.fuse
    : undefined
}

function getHeatingPower(catalogProduct: CatalogProduct) {
  return isHeaterCatalogProduct(catalogProduct) || isAuroraCatalogProduct(catalogProduct)
    ? catalogProduct.customData?.heatingPower
    : undefined
}

export interface CatalogProductAutocompleteProps extends Omit<
  AutocompleteProps<CatalogProduct, false, false, false>,
  'options' | 'value' | 'renderInput' | 'groupBy'
> {
  type?: CatalogProductType
  query?: CatalogProductQuery
  filterBy?: (catalogProduct: CatalogProduct) => boolean
  value?: string | null
  label?: string
  placeholder?: string
  options?: CatalogProduct[]
  groupBy?: CatalogProductGroupBy
  withPreview?: boolean
  dedupeByManufacturerProductCode?: boolean
}

export default function CatalogProductAutocomplete({
  type,
  query = {},
  filterBy,
  value,
  label,
  placeholder,
  options,
  groupBy,
  dedupeByManufacturerProductCode = false,
  inputValue,
  withPreview = false,
  ...props
}: CatalogProductAutocompleteProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The catalog products state.
   */
  const [catalogProducts, setCatalogProducts] = useState<CatalogProduct[]>([])
  const {
    type: queryType,
    status: queryStatus,
    manufacturer: queryManufacturer,
    fuse: queryFuse,
    heatingPower: queryHeatingPower
  } = query

  const resolvedCatalogProducts = dedupeByManufacturerProductCode
    ? dedupeCatalogProductsByManufacturerProductCode(options ?? catalogProducts, value)
    : (options ?? catalogProducts)

  const selectedCatalogProductName =
    resolvedCatalogProducts.find((product) => product.id === value)?.name ?? value ?? ''

  const selectedCatalogProduct =
    resolvedCatalogProducts.find((product) => product.id === value) ?? null

  /**
   * The sorted options.
   */
  const sortedOptions = sortOptions(resolvedCatalogProducts)

  /**
   * Sort the options.
   */
  function sortOptions(options: CatalogProduct[]) {
    return options.toSorted((a, b) => {
      if (groupBy === 'manufacturer') {
        return a.manufacturer.localeCompare(b.manufacturer)
      } else if (groupBy === 'fuse') {
        return (getFuse(a) ?? 0) - (getFuse(b) ?? 0)
      } else if (groupBy === 'heatingPower') {
        return (getHeatingPower(a) ?? 0) - (getHeatingPower(b) ?? 0)
      }

      return a.name.localeCompare(b.name)
    })
  }

  function groupByOption(option: CatalogProduct) {
    if (groupBy === 'fuse') {
      return `${getFuse(option)}A`
    } else if (groupBy === 'heatingPower') {
      return `${getHeatingPower(option)} kW`
    } else {
      return option.manufacturer
    }
  }

  function dedupeCatalogProductsByManufacturerProductCode(
    products: CatalogProduct[],
    selectedValue?: string | null
  ) {
    const productsWithoutCode: CatalogProduct[] = []
    const dedupedProducts = new Map<string, CatalogProduct>()

    for (const product of products) {
      const normalizedManufacturerProductCode = product.manufacturerProductCode
        ?.trim()
        .toLowerCase()

      if (!normalizedManufacturerProductCode) {
        productsWithoutCode.push(product)
        continue
      }

      const existingProduct = dedupedProducts.get(normalizedManufacturerProductCode)

      if (
        existingProduct === undefined ||
        (product.id === selectedValue && existingProduct.id !== selectedValue)
      ) {
        dedupedProducts.set(normalizedManufacturerProductCode, product)
      }
    }

    return [...productsWithoutCode, ...dedupedProducts.values()]
  }

  /**
   * Load the catalog products.
   */
  async function loadCatalogProducts(query: CatalogProductQuery, controller?: AbortController) {
    try {
      let catalogProducts = await getCatalogProducts(query, { signal: controller?.signal })

      if (filterBy !== undefined) {
        catalogProducts = catalogProducts.filter(filterBy)
      }

      setCatalogProducts(catalogProducts)
    } catch (_) {
      // Do nothing.
    }
  }

  /**
   * Load the catalog products when the component mounts unless options are provided.
   */
  useEffect(() => {
    if (options === undefined) {
      const controller = new AbortController()

      loadCatalogProducts(
        {
          status: queryStatus ?? CatalogProductStatus.VERIFIED,
          type: queryType ?? type,
          manufacturer: queryManufacturer,
          fuse: queryFuse,
          heatingPower: queryHeatingPower
        },
        controller
      )

      return () => controller.abort()
    } else {
      setCatalogProducts(options)
    }
  }, [options, type, queryStatus, queryManufacturer, queryFuse, queryHeatingPower, queryType])

  return (
    <>
      <Autocomplete
        options={sortedOptions}
        inputValue={inputValue ?? selectedCatalogProductName}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
        getOptionKey={(option) => (typeof option === 'string' ? option : option.id)}
        noOptionsText={null}
        groupBy={groupBy ? groupByOption : undefined}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={(props, option) => (
          <MenuItem {...props}>
            <ListItemText
              primary={option.name}
              primaryTypographyProps={{
                noWrap: true,
                sx: { order: '1 !important' }
              }}
              secondary={
                option.manufacturerProductCode
                  ? option.manufacturerProductCode
                  : option.manufacturer
              }
              secondaryTypographyProps={{
                noWrap: true,
                sx: { order: '1 !important' }
              }}
            />
          </MenuItem>
        )}
        renderInput={(params) => (
          <TextField
            label={label ?? t('selectCatalogProduct')}
            placeholder={placeholder}
            {...params}
            // InputProps={{
            //   endAdornment: null
            // }}
          />
        )}
        value={sortedOptions.find((option) => option.id === value) ?? null}
        {...props}
      />

      {withPreview && selectedCatalogProduct && (
        <>
          <CatalogProductDetailsList catalogProduct={selectedCatalogProduct} />
          {/*<Accordion sx={{ boxShadow: 0 }}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ m: '0!important' }}>
              {t('actions:showDetails')}
            </AccordionSummary>
            <AccordionDetails>
              <CatalogProductDetailsList catalogProduct={selectedCatalogProduct} />
            </AccordionDetails>
          </Accordion>*/}
        </>
      )}
    </>
  )
}
